import { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { AuthShell } from '@/components/AuthShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { HubClientError } from '@verybigthings/trackframe-hub-client';
import { hub } from '@/lib/hub-client';

/**
 * Public set-password page an invite email links to (HUB-106):
 * `/login/invite?token=…`. One password input with a reveal toggle — no
 * repeat field. The hub burns the token, revokes any prior session and
 * answers a fresh one (cookies), so success lands the user in the app
 * signed in — no second password prompt.
 */
export function inviteTokenFromSearch(search: string): string | null {
  const token = new URLSearchParams(search).get('token');
  return token && token.length > 0 ? token : null;
}

const EXPIRED_MESSAGE = 'This link is no longer valid. Ask your administrator to send a new invite.';

export function PasswordInput({
  id,
  value,
  onChange,
  revealed,
  onToggle,
  autoFocus,
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
  revealed: boolean;
  onToggle: () => void;
  autoFocus?: boolean;
}) {
  return (
    <div className="relative">
      <Input
        id={id}
        type={revealed ? 'text' : 'password'}
        autoComplete="new-password"
        required
        minLength={8}
        maxLength={256}
        autoFocus={autoFocus}
        className="pr-10"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      <button
        type="button"
        onClick={onToggle}
        aria-label={revealed ? 'Hide password' : 'Show password'}
        aria-pressed={revealed}
        className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {revealed ? <EyeOff className="size-4" aria-hidden="true" /> : <Eye className="size-4" aria-hidden="true" />}
      </button>
    </div>
  );
}

export function Invite({ token }: { token: string | null }) {
  const [password, setPassword] = useState('');
  const [revealed, setRevealed] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!token) {
    return (
      <AuthShell title="Set your password" subtitle="Trackframe">
        <p className="text-sm text-destructive">{EXPIRED_MESSAGE}</p>
      </AuthShell>
    );
  }

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setPending(true);
    try {
      // Server sets tf_sso + tf_refresh on the response, exactly like login.
      await hub.authPasswordResetConfirm({ body: { token, newPassword: password } });
      window.location.replace('/');
    } catch (err) {
      const expired = err instanceof HubClientError && err.code === 'token_invalid';
      setError(expired ? EXPIRED_MESSAGE : ((err as Error).message ?? 'Could not set the password'));
      setPending(false);
    }
  };

  return (
    <AuthShell title="Set your password" subtitle="Trackframe">
      <form className="space-y-4" onSubmit={onSubmit}>
        <div className="space-y-2">
          <Label htmlFor="new-password">New password</Label>
          <PasswordInput
            id="new-password"
            value={password}
            onChange={setPassword}
            revealed={revealed}
            onToggle={() => setRevealed((r) => !r)}
            autoFocus
          />
          <p className="text-xs text-muted-foreground">At least 8 characters.</p>
        </div>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <Button type="submit" className="w-full" disabled={pending}>
          {pending ? 'Saving…' : 'Set password and sign in'}
        </Button>
      </form>
    </AuthShell>
  );
}

/** Route element: reads the token from the current URL. */
export function InvitePage() {
  return <Invite token={inviteTokenFromSearch(typeof window === 'undefined' ? '' : window.location.search)} />;
}
